/* ==========================================================================
   The Tint Therapyy — motion runtime
   --------------------------------------------------------------------------
   Four primitives, one scroll loop, one observer.

     [data-tt-reveal]    fade / scale / mask reveal on entry
     [data-tt-parallax]  translates its first child against scroll
     [data-tt-scale]     scales its image between two viewport positions
     .tt-cursor          hairline pointer companion (fine pointers only)

   Design rules this file follows:
     • Content is visible by default. The `tt-motion` class that hides
       revealable elements is only added once this script is running, so a
       parse error or blocked asset leaves the page fully readable.
     • One rAF loop drives every transform. No per-element listeners.
     • Everything is disabled under prefers-reduced-motion, and re-checked
       if the visitor changes that setting mid-session.
     • Elements are unobserved after they reveal. Nothing accumulates.
   ========================================================================== */

(() => {
  'use strict';

  const root = document.documentElement;
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /** Elements taking part in the scroll loop. */
  let parallaxItems = [];
  let scaleItems = [];
  let rafId = null;
  let observer = null;
  let cursor = null;

  const prefersReduced = () => motionQuery.matches;

  /* ---------------------------------------------------------------- reveal */

  function initReveals(scope = document) {
    if (!('IntersectionObserver' in window)) return;

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
      );
    }

    scope.querySelectorAll('[data-tt-reveal]:not(.is-revealed)').forEach((el) => {
      // Anything already on screen at load reveals immediately rather than
      // waiting for a scroll that may never come on a short page.
      const box = el.getBoundingClientRect();
      if (box.top < window.innerHeight * 0.92 && box.bottom > 0) {
        el.classList.add('is-revealed');
        return;
      }
      observer.observe(el);
    });

    // Index staggered children so CSS can offset their delays.
    scope.querySelectorAll('[data-tt-stagger]').forEach((group) => {
      Array.from(group.children).forEach((child, i) => {
        child.style.setProperty('--tt-i', String(i));
      });
    });
  }

  /* -------------------------------------------------------------- scroll fx */

  function collectScrollItems() {
    parallaxItems = Array.from(document.querySelectorAll('[data-tt-parallax]')).map((el) => ({
      el,
      depth: parseFloat(el.dataset.ttParallax) || 0.14,
    }));

    scaleItems = Array.from(document.querySelectorAll('[data-tt-scale]')).map((el) => ({
      el,
      from: parseFloat(el.dataset.ttScaleFrom) || 1.08,
      to: parseFloat(el.dataset.ttScaleTo) || 1.0,
    }));
  }

  function tick() {
    rafId = null;
    const vh = window.innerHeight || 1;

    for (const item of parallaxItems) {
      const box = item.el.getBoundingClientRect();
      if (box.bottom < -200 || box.top > vh + 200) continue;
      // -1 when the element sits below the fold, +1 when it has passed above.
      const progress = (vh / 2 - (box.top + box.height / 2)) / (vh / 2 + box.height / 2);
      const offset = progress * item.depth * box.height * 0.5;
      item.el.style.setProperty('--tt-parallax', `${offset.toFixed(2)}px`);
    }

    for (const item of scaleItems) {
      const box = item.el.getBoundingClientRect();
      if (box.bottom < -200 || box.top > vh + 200) continue;
      const raw = 1 - (box.top + box.height) / (vh + box.height);
      const p = Math.min(1, Math.max(0, raw));
      const scale = item.from + (item.to - item.from) * p;
      item.el.style.setProperty('--tt-scale', scale.toFixed(4));
    }
  }

  function schedule() {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(tick);
  }

  /* ---------------------------------------------------------------- cursor */

  function initCursor() {
    if (!finePointer.matches || prefersReduced()) return;
    if (cursor) return;

    cursor = document.createElement('div');
    cursor.className = 'tt-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.innerHTML = '<span class="tt-cursor__label">View</span>';
    document.body.appendChild(cursor);

    let tx = 0, ty = 0, cx = 0, cy = 0, cursorRaf = null;

    const render = () => {
      cursorRaf = null;
      // Critically damped follow. 0.18 reads as "attached but unhurried".
      cx += (tx - cx) * 0.18;
      cy += (ty - cy) * 0.18;
      cursor.style.setProperty('--tt-cx', `${cx.toFixed(2)}px`);
      cursor.style.setProperty('--tt-cy', `${cy.toFixed(2)}px`);
      if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
        cursorRaf = window.requestAnimationFrame(render);
      }
    };

    const onMove = (event) => {
      tx = event.clientX;
      ty = event.clientY;
      cursor.dataset.visible = 'true';
      if (cursorRaf === null) cursorRaf = window.requestAnimationFrame(render);

      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-tt-cursor="media"], .tt-artwork__plate, .product-media')) {
        cursor.dataset.mode = 'media';
      } else if (target.closest('a, button, [role="button"], summary, label')) {
        cursor.dataset.mode = 'link';
      } else {
        cursor.removeAttribute('data-mode');
      }
    };

    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerdown', () => { cursor.dataset.mode = 'link'; }, { passive: true });
    document.addEventListener('mouseleave', () => { cursor.dataset.visible = 'false'; });
  }

  function destroyCursor() {
    if (!cursor) return;
    cursor.remove();
    cursor = null;
  }

  /* ------------------------------------------------------------------ boot */

  function enable() {
    if (prefersReduced()) {
      root.classList.remove('tt-motion');
      destroyCursor();
      return;
    }
    root.classList.add('tt-motion');
    initReveals();
    collectScrollItems();
    initCursor();
    schedule();
  }

  function refresh() {
    initReveals();
    collectScrollItems();
    schedule();
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', () => { collectScrollItems(); schedule(); }, { passive: true });

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', enable);
  }

  // Shopify's section rendering swaps DOM without a page load; re-scan.
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
  document.addEventListener('shopify:block:select', refresh);

  // Horizon's view transitions replace <main> in place.
  window.addEventListener('pageshow', refresh);
  document.addEventListener('tint:refresh', refresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enable, { once: true });
  } else {
    enable();
  }

  // Expose a tiny hook so sections can re-register after injecting markup.
  window.TintMotion = { refresh, enable };
})();
